import { Dot } from "components/Dot";
import { cn } from "helpers/classNames";
import { Link, type LinkProps, useNavigate } from "@tanstack/react-router";

export type TabListItem = {
  label: string;
  value?: string;
  isSelected?: boolean;
  isActive?: boolean;
  isDisabled?: boolean;
  ping?: boolean;
  link?: Pick<LinkProps, "to" | "params" | "search">;
};
type Props = {
  items: TabListItem[];
  className?: string;
};

export const TabMenu = ({ items, className }: Props) => {
  const navigate = useNavigate();

  const isTabSelected = (tab: TabListItem) =>
    tab.isActive ?? tab.isSelected ?? false;

  return (
    <div className={cn("mb-4", className)}>
      <div className="grid grid-cols-1 sm:hidden">
        {/* Mobile select dropdown */}
        <select
          value={items.find((tab) => isTabSelected(tab))?.value}
          aria-label="Select a tab"
          onChange={(e) => {
            const selectedTab = items.find(
              (tab) => tab.value === e.target.value,
            );
            if (selectedTab?.link) {
              navigate(selectedTab.link);
            }
          }}
          className="col-start-1 row-start-1 w-full appearance-none rounded-md bg-white py-2 pl-3 pr-8 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-cyan-600"
        >
          {items.map((tab) => (
            <option key={tab.label} value={tab.value} disabled={tab.isDisabled}>
              {tab.label}
            </option>
          ))}
        </select>
      </div>
      <div className="hidden rounded-lg border-b border-gray-200 bg-white sm:inline-block">
        <div className="flex items-center justify-between pr-8">
          <nav className="-mb-px flex space-x-8 pl-8" aria-label="Tabs">
            {items.map((tab) => {
              const selected = isTabSelected(tab);
              return (
                <Link
                  key={tab.label}
                  {...tab.link}
                  className={cn(
                    selected
                      ? "border-cyan-500 font-semibold text-cyan-600"
                      : "border-transparent font-semibold text-gray-500 hover:border-gray-300 hover:text-gray-700",
                    "relative flex whitespace-nowrap border-b-2 px-1 py-4 text-sm tracking-wide",
                  )}
                  aria-current={selected ? "page" : undefined}
                  disabled={tab.isDisabled}
                >
                  <span className="flex items-center">
                    {tab.label}
                    {tab.ping && <Dot className="ml-1" />}
                  </span>
                  <span
                    aria-hidden="true"
                    className={cn(
                      selected ? "bg-cyan-500" : "bg-transparent",
                      "absolute inset-x-0 bottom-0 h-0.5",
                    )}
                  />
                </Link>
              );
            })}
          </nav>
        </div>
      </div>
    </div>
  );
};
